import React, { useEffect, useState, useCallback } from "react";
import Filters from "../components/Filters";
import { EmptyReport, ErrorState } from "../components/EmptyState";
import A5Matrix from "../components/A5Matrix";
import { api, monthLabel } from "../lib/api";
import { loadSignatures } from "../lib/signatures";
import { Info } from "lucide-react";

export default function BukuA5() {
  const [filter, setFilter] = useState({ tahun: "2026", bulan: "september", rw: "", rt: "" });
  const [doc, setDoc] = useState(null); // { data, scope, rw, rt, signature }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const scope = (() => {
    if (filter.rw && filter.rt) return "rt";
    if (filter.rw) return "rw";
    return "kelurahan";
  })();

  const load = useCallback(async () => {
    setLoading(true); setError(false); setDoc(null);
    const periode = `${monthLabel(filter.bulan)} ${filter.tahun}`;
    try {
      if (scope === "rt") {
        const r = await api.a5(filter.tahun, filter.bulan, filter.rw, filter.rt);
        if (r?.data) {
          const sig = await loadSignatures("rt", filter.rw, filter.rt, filter.bulan, filter.tahun);
          setDoc({ data: r.data, scope, rw: filter.rw, rt: filter.rt, signature: sig, periode, bulan: filter.bulan, tahun: filter.tahun });
        } else {
          setDoc({ empty: true });
        }
      } else if (scope === "rw") {
        const r = await api.a5Agregat(filter.tahun, filter.bulan, "rw", filter.rw);
        if (r?.data) {
          const sig = await loadSignatures("rw", filter.rw, null, filter.bulan, filter.tahun);
          setDoc({ data: r.data, scope, rw: filter.rw, rt: "SELURUH RT", signature: sig, periode, bulan: filter.bulan, tahun: filter.tahun });
        } else {
          setDoc({ empty: true });
        }
      } else {
        const r = await api.a5Agregat(filter.tahun, filter.bulan, "kelurahan");
        if (r?.data) {
          const sig = await loadSignatures("kelurahan", null, null, filter.bulan, filter.tahun);
          setDoc({ data: r.data, scope, rw: "SELURUH RW", rt: "-", signature: sig, periode, bulan: filter.bulan, tahun: filter.tahun });
        } else {
          setDoc({ empty: true });
        }
      }
    } catch { setError(true); } finally { setLoading(false); }
  }, [filter.tahun, filter.bulan, filter.rw, filter.rt, scope]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Buku Matriks A.5</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Periode <strong>{monthLabel(filter.bulan)} {filter.tahun}</strong> ·
          Mode: <strong>{scope === "kelurahan" ? "Seluruh Kelurahan" : scope === "rw" ? `RW ${filter.rw}` : `RW ${filter.rw} / RT ${filter.rt}`}</strong>
        </p>
      </div>

      <Filters value={filter} onChange={setFilter} onRefresh={load} showRw showRt />

      <div className="rounded-lg bg-[#f0f9f4] border border-[#c8ecd4] p-3 flex items-start gap-2 text-sm text-[#0e6b4f]">
        <Info size={16} className="shrink-0 mt-0.5" />
        <div>
          Pilih <strong>RW</strong> saja untuk agregasi seluruh RT dalam RW, atau <strong>RW + RT</strong> untuk dokumen A.5 asli.
          Tanpa filter, ditampilkan agregasi seluruh kelurahan.
        </div>
      </div>

      {error && <ErrorState onRetry={load} />}

      {!error && loading && (
        <div className="bg-white rounded-xl border p-8"><div className="skeleton h-64 w-full"></div></div>
      )}

      {!error && !loading && doc?.empty && <EmptyReport />}

      {!error && !loading && doc?.data && (
        <A5Matrix
          data={doc.data}
          periode={doc.periode}
          rw={doc.rw}
          rt={doc.rt}
          scope={doc.scope}
          signature={doc.signature}
          bulan={doc.bulan}
          tahun={doc.tahun}
        />
      )}
    </div>
  );
}
